import { EChartsOption } from 'echarts';
import {
  CHART_CHROME,
  OUTCOME_SERIES,
  SINGLE_SERIES,
  outcomeSeriesColor,
} from './cancellations.palette';
import {
  CancellationFollowUpGroup,
  CancellationReasonStat,
  CancellationTimelinePoint,
  OffboardingOutcome,
} from './cancellations.types';

/** Rounded data-end, per the mark spec. */
const END_RADIUS = 4;
/** Surface-coloured gap between stacked segments — a gap, not a border. */
const SEGMENT_GAP = 2;

// ------------------------------------------------------------- formatting

/**
 * Minor units to a readable amount.
 *
 * No decimals: these are monthly subscription totals read at a glance, and
 * "£4,180" beats "£4,180.00" in a KPI card. The currency comes off the row —
 * brands bill in gbp and usd and the dashboard reports each separately.
 */
export function formatMinorUnits(
  minorUnits: number | null,
  currencyCode: string | null,
): string {
  if (minorUnits === null) return '—';

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: (currencyCode ?? 'gbp').toUpperCase(),
    maximumFractionDigits: 0,
  }).format(minorUnits / 100);
}

/** A 0-1 rate as a whole percentage, or an em dash when it is genuinely unknown. */
export function formatRate(rate: number | null): string {
  if (rate === null) return '—';
  return `${Math.round(rate * 100)}%`;
}

export function formatPercent(fraction: number): string {
  const percent = fraction * 100;
  return `${percent >= 10 || percent === 0 ? Math.round(percent) : percent.toFixed(1)}%`;
}

export function formatRangeLabel(from: string, to: string): string {
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const start = new Date(from);
  // The API's `to` is exclusive, so the last day people actually see is the one
  // before it — showing the exclusive bound would read as a day too many.
  const end = new Date(new Date(to).getTime() - 86_400_000);

  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const startLabel = start.toLocaleDateString('en-GB', options);
  const endLabel = end.toLocaleDateString(
    'en-GB',
    sameYear ? options : { ...options, year: 'numeric' },
  );

  return `${startLabel} – ${endLabel}`;
}

/** Bucket boundary as an axis tick. */
export function formatBucketLabel(date: string, bucket: 'day' | 'week'): string {
  const value = new Date(date);
  return bucket === 'day'
    ? value.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : `w/c ${value.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
}

// ------------------------------------------------------------------ chips

const OUTCOME_LABELS: Record<OffboardingOutcome, string> = {
  started: 'Abandoned',
  cancelled: 'Cancelled',
  paused: 'Paused',
  kept: 'Saved',
  reactivated: 'Reactivated',
  pause_cancelled: 'Pause cancelled',
};

export function outcomeLabel(outcome: OffboardingOutcome): string {
  return OUTCOME_LABELS[outcome] ?? outcome;
}

/**
 * Chip styling for the six raw outcomes.
 *
 * Six, not the four chart series: `reactivated` and `pause_cancelled` are folded
 * into their parent decision for counting, but on an individual row they are the
 * most interesting thing about it and must not be disguised as an ordinary
 * cancellation. The chip carries its label, so colour is never the only channel.
 */
export function outcomeChipClass(outcome: OffboardingOutcome): string {
  switch (outcome) {
    case 'cancelled':
      return 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20';
    case 'kept':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20';
    case 'paused':
      return 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20';
    case 'reactivated':
      return 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20';
    case 'pause_cancelled':
      return 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-400/20';
    default:
      return 'bg-zinc-100 text-zinc-600 ring-zinc-500/20 dark:bg-white/5 dark:text-zinc-300 dark:ring-white/10';
  }
}

export function scopeLabel(scope: string): string {
  return scope === 'agency' ? 'Agency' : 'Brand';
}

// ----------------------------------------------------------------- charts

function chrome(isDark: boolean) {
  return isDark ? CHART_CHROME.dark : CHART_CHROME.light;
}

function baseTooltip(isDark: boolean) {
  return {
    backgroundColor: isDark ? '#1f1f2b' : '#101828',
    borderColor: isDark ? '#2c2c3a' : '#101828',
    textStyle: { color: '#f3f4f6', fontSize: 12 },
    // Bar hit areas are small at these counts; widen them past the mark.
    extraCssText: 'border-radius:8px;padding:8px 10px;',
  };
}

function recessiveAxis(isDark: boolean) {
  const tone = chrome(isDark);
  return {
    axisLine: { show: true, lineStyle: { color: tone.axis, width: 1 } },
    axisTick: { show: false },
    axisLabel: { color: tone.muted, fontSize: 11 },
    splitLine: { show: false },
  };
}

/**
 * Reasons, ranked, each bar split by what the account went on to do.
 *
 * One chart rather than two: the bar length is the reason distribution and the
 * segments are the outcome split, so "which objection is biggest" and "which
 * objections do we actually beat" are answered in the same glance. A donut was
 * the obvious alternative and is the wrong form — six slices of similar size is
 * exactly what a pie cannot show.
 */
export function buildReasonOutcomeChart(
  reasons: CancellationReasonStat[],
  isDark: boolean,
): { option: EChartsOption; hasData: boolean } {
  const withData = reasons.filter((reason) => reason.count > 0);
  const hasData = withData.length > 0;

  // ECharts draws a horizontal bar's first category at the bottom, so the
  // ranking has to be reversed to read top-down.
  const ordered = [...withData].reverse();
  const tone = chrome(isDark);

  const series = OUTCOME_SERIES.map((entry, seriesIndex) => ({
    name: entry.label,
    type: 'bar' as const,
    stack: 'outcome',
    barMaxWidth: 18,
    data: ordered.map((reason) => {
      const values = OUTCOME_SERIES.map((candidate) => reason[candidate.statKey]);
      const value = values[seriesIndex];
      const firstFilled = values.findIndex((entryValue) => entryValue > 0);
      const lastFilled = values.length - 1 - [...values].reverse().findIndex((entryValue) => entryValue > 0);

      return {
        value,
        itemStyle: {
          color: outcomeSeriesColor(entry.key, isDark),
          // Only the outer ends of the whole bar are rounded — rounding every
          // segment would read as separate bars rather than one split total.
          borderRadius: [
            seriesIndex === firstFilled ? END_RADIUS : 0,
            seriesIndex === lastFilled ? END_RADIUS : 0,
            seriesIndex === lastFilled ? END_RADIUS : 0,
            seriesIndex === firstFilled ? END_RADIUS : 0,
          ] as [number, number, number, number],
          borderColor: tone.surface,
          borderWidth: value > 0 ? SEGMENT_GAP : 0,
        },
      };
    }),
  }));

  return {
    hasData,
    option: {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...baseTooltip(isDark) },
      legend: {
        show: true,
        bottom: 0,
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 16,
        textStyle: { color: tone.muted, fontSize: 11 },
        data: OUTCOME_SERIES.map((entry) => entry.label),
      },
      grid: { left: 4, right: 48, top: 8, bottom: 32, containLabel: true },
      xAxis: { type: 'value', minInterval: 1, ...recessiveAxis(isDark), splitLine: { show: true, lineStyle: { color: tone.grid, width: 1 } } },
      yAxis: {
        type: 'category',
        data: ordered.map((reason) => reason.label),
        ...recessiveAxis(isDark),
        axisLabel: { color: tone.muted, fontSize: 11, width: 130, overflow: 'truncate' },
      },
      series,
    },
  };
}

/**
 * Follow-up chips for one reason.
 *
 * Single series, so no legend — the panel heading names it. Every option the
 * survey offered is plotted, including the ones nobody picked: a zero is a
 * finding, and dropping it would make two reasons' charts silently
 * incomparable.
 */
export function buildFollowUpChart(
  group: CancellationFollowUpGroup | null,
  isDark: boolean,
): { option: EChartsOption; hasData: boolean } {
  const options = group?.options ?? [];
  const hasData = options.some((option) => option.count > 0);
  const ordered = [...options].reverse();
  const tone = chrome(isDark);

  return {
    hasData,
    option: {
      tooltip: { trigger: 'item', ...baseTooltip(isDark) },
      grid: { left: 4, right: 32, top: 8, bottom: 8, containLabel: true },
      xAxis: { type: 'value', minInterval: 1, ...recessiveAxis(isDark), splitLine: { show: true, lineStyle: { color: tone.grid, width: 1 } } },
      yAxis: {
        type: 'category',
        data: ordered.map((option) => option.label),
        ...recessiveAxis(isDark),
        axisLabel: { color: tone.muted, fontSize: 11, width: 150, overflow: 'truncate' },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 16,
          data: ordered.map((option) => option.count),
          itemStyle: {
            color: isDark ? SINGLE_SERIES.dark : SINGLE_SERIES.light,
            borderRadius: [0, END_RADIUS, END_RADIUS, 0],
          },
          label: {
            show: true,
            position: 'right',
            // Text wears a text token, never the series colour.
            color: tone.muted,
            fontSize: 11,
          },
        },
      ],
    },
  };
}

/**
 * Off-boardings over time.
 *
 * Bars rather than lines: these are small integer counts that spend most of
 * their time at zero, and a line through mostly-zero data draws a trend that
 * is not there.
 */
export function buildTimelineChart(
  points: CancellationTimelinePoint[],
  bucket: 'day' | 'week',
  isDark: boolean,
): { option: EChartsOption; hasData: boolean } {
  const hasData = points.some(
    (point) => point.cancelled + point.paused + point.kept + point.started > 0,
  );
  const tone = chrome(isDark);

  return {
    hasData,
    option: {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...baseTooltip(isDark) },
      legend: {
        show: true,
        bottom: 0,
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 16,
        textStyle: { color: tone.muted, fontSize: 11 },
        data: OUTCOME_SERIES.map((entry) => entry.label),
      },
      grid: { left: 4, right: 8, top: 8, bottom: 32, containLabel: true },
      xAxis: {
        type: 'category',
        data: points.map((point) => formatBucketLabel(point.date, bucket)),
        ...recessiveAxis(isDark),
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        ...recessiveAxis(isDark),
        splitLine: { show: true, lineStyle: { color: tone.grid, width: 1 } },
      },
      series: OUTCOME_SERIES.map((entry, seriesIndex) => ({
        name: entry.label,
        type: 'bar' as const,
        stack: 'outcome',
        barMaxWidth: 28,
        itemStyle: {
          color: outcomeSeriesColor(entry.key, isDark),
          borderColor: tone.surface,
          borderWidth: SEGMENT_GAP,
          borderRadius:
            seriesIndex === OUTCOME_SERIES.length - 1
              ? ([END_RADIUS, END_RADIUS, 0, 0] as [number, number, number, number])
              : 0,
        },
        data: points.map((point) => point[entry.key]),
      })),
    },
  };
}
